import { push } from '@lagunovsky/redux-react-router'
import cx from 'classnames'
import { useFormik } from 'formik'
import { useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'

import ButtonCinestar from '../../Buttons/ButtonCinestar/ButtonCinestar'
import ButtonLink from '../../Buttons/ButtonLink/ButtonLink'
import InputEmail from '../../Inputs/InputEmail/InputEmail'
import InputPassword from '../../Inputs/InputPassword/InputPassword'
import FormLayout from '../FormLayout/FormLayout'
import FormLine from '../FormLine/FormLine'

import AppleLoginBtn from 'components/Buttons/ButtonSocialLogin/AppleLoginBtn'
import FacebookLoginBtn from 'components/Buttons/ButtonSocialLogin/FacebookLoginBtn'
import GoogleLoginBtn from 'components/Buttons/ButtonSocialLogin/GoogleLoginBtn'

import gtmForgotPasswordStart from 'services/gtm/gtmForgotPasswordStart'
import gtmLoginClicked from 'services/gtm/gtmLoginClicked'
import gtmStartRegistration from 'services/gtm/gtmStartRegistration'
import useGtmLoginViewed from 'services/gtm/useGtmLoginViewed'
import './FormLogin.scss'

const FormLogin = ({
    title,
    loading,
    formErrors,
    handleSubmit,
    handleFacebook,
    handleGoogle,
    handleApple,
    handleSocialsError,
    className,
}) => {
    const intl = useIntl()
    const dispatch = useDispatch()
    const formik = useFormik({
        initialValues: {
            email: '',
            password: '',
        },
        validationSchema: Yup.object({
            email: Yup.string()
                .email(intl.formatMessage({ id: 'forms.login.emailError' }))
                .required(intl.formatMessage({ id: 'forms.required' })),
            password: Yup.string().required(intl.formatMessage({ id: 'forms.required' })),
        }),
        onSubmit: (values) => {
            handleSubmit(values)
        },
    })

    useGtmLoginViewed()

    useEffect(() => {
        if (formErrors?.email) formik.setFieldError('email', formErrors?.email)
        if (formErrors?.password) formik.setFieldError('password', formErrors?.password)
    }, [formErrors])

    return (
        <FormLayout
            title={title}
            onSubmit={formik.handleSubmit}
            className={cx('form-login d-flex flex-column', className)}
        >
            <div className="social-btns-wrapper">
                <FacebookLoginBtn onSuccess={handleFacebook} onError={handleSocialsError} disabled={loading} />
                <GoogleLoginBtn onSuccess={handleGoogle} onError={handleSocialsError} disabled={loading} />
                <AppleLoginBtn onSuccess={handleApple} />
            </div>

            <FormLine text={intl.formatMessage({ id: 'forms.login.orFillText' })} />
            <InputEmail
                id={'email'}
                name={'email'}
                data-test="loginEmail"
                autoComplete={'email'}
                error={formik?.errors?.email}
                touched={formik?.touched?.email}
                {...formik.getFieldProps('email')}
                label={intl.formatMessage({ id: 'forms.login.emailLabel' })}
                placeholder={intl.formatMessage({ id: 'forms.login.emailPlaceholder' })}
                disabled={loading}
                onBlur={formik?.handleBlur}
            />
            <InputPassword
                id={'password'}
                name={'password'}
                data-test="loginPassword"
                autoComplete={'password'}
                touched={formik?.touched?.password}
                error={formik?.errors?.password}
                {...formik.getFieldProps('password')}
                label={intl.formatMessage({ id: 'forms.login.passwordLabel' })}
                placeholder={intl.formatMessage({ id: 'forms.login.passwordPlaceholder' })}
                disabled={loading}
                onBlur={formik?.handleBlur}
            />
            <div className={'buttons-login d-flex'}>
                <ButtonCinestar
                    data-test="loginSubmit"
                    variant={'primary-v1'}
                    type={'submit'}
                    disabled={loading}
                    onClick={() => gtmLoginClicked()}
                >
                    {intl.formatMessage({ id: 'forms.login.buttonLogin' })}
                </ButtonCinestar>
                <ButtonLink
                    onClick={() => {
                        gtmForgotPasswordStart(), dispatch(push('/forgotten-password'))
                    }}
                    className="px-1"
                    disabled={loading}
                >
                    {intl.formatMessage({ id: 'forms.login.buttonForgotten' })}
                </ButtonLink>
            </div>
            <div className={'border-top border-darkerPrimary'}></div>
            <ButtonLink
                onClick={() => {
                    gtmStartRegistration()
                    dispatch(push('/register'))
                }}
                className="py-1"
                disabled={loading}
            >
                {intl.formatMessage({ id: 'forms.login.buttonCreate' })}
            </ButtonLink>
        </FormLayout>
    )
}

export default FormLogin
